import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import omit from 'lodash/omit';

import {
    Tag,
    TextButton,
    Spacer,
    Alert,
    Text,
    Button,
    IconCheckmark,
    IconClose,

} from '@zillow/constellation';
import { HR, Row, Col } from '@zillow/zrs-comet';

import { RMWContext } from 'app/shared/providers/RMWContext';
import { UserManagementSelectors, UserSelectors } from 'app/shared/selectors';
import ModalActions from 'app/shared/actions/ModalActions';
import ModalConstants from 'app/shared/constants/ModalConstants';
import UserManagementActions from 'app/shared/actions/UserManagementActions';
import UserPermissionsConstants from 'app/shared/constants/UserPermissionsConstants';

const propTypes = {
    onSendInvite: PropTypes.func.isRequired,
    onBack: PropTypes.func.isRequired,
};

const Preview = ({
    onSendInvite,
    onBack,
    onEditRole,
    onEditAllProperties,
    onEditProperties,
    onEditPermissions,
    currentUser,
    dispatch,
    user,
    onCancel,
}) => {
    const [error, setError] = React.useState(null);
    const { paths } = React.useContext(RMWContext);
    const isAdmin = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN];
    const allProperties = currentUser.permissions[UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES] || isAdmin;
    const { isInvitation, isPending } = currentUser;
    const isYou = !isInvitation && !isPending && user.userToken === currentUser.userToken;
    const otherPermissions = omit(UserPermissionsConstants.PERMISSIONS, [
        UserPermissionsConstants.PERMISSIONS.COMPANY_ADMIN,
        UserPermissionsConstants.PERMISSIONS.SYNC_PROPERTIES,
        UserPermissionsConstants.PERMISSIONS.COMPANY_USER,
    ]);

    const handleCancelInvitation = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            children: 'Are you sure you want to cancel this invite?',
            submitButtonTitle: 'Confirm',
            header: 'Cancel invite',
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                dispatch(UserManagementActions.cancelInvitation(currentUser.email))
                    .then(() => {
                        window.router.transitionTo(`${paths.userManagementPage}/${paths.userManagementPending}`);
                        dispatch(ModalActions.hideModal());
                    })
                    .catch(() => {
                        // Todo: show error
                    });
            },
        }));
    };

    const handleDeactivateAccount = () => {
        dispatch(ModalActions.showModal({
            modalType: ModalConstants.CONFIRMATION_MODAL,
            textContent: 'Are you sure you want to deactivate this user?',
            submitButtonTitle: 'Confirm',
            header: 'Deactivate user',
            onCancel: () => dispatch(ModalActions.hideModal()),
            onSubmit: () => {
                dispatch(UserManagementActions.deactivateUser())
                    .then(() => {
                        dispatch(ModalActions.hideModal());
                        window.router.transitionTo(`${paths.userManagementPage}/${paths.userManagementUsers}`);
                    })
                    .catch((e) => {
                        setError((e && e.message) || 'Failed to remove user, please try again later.');
                        dispatch(ModalActions.hideModal());
                    });
            },
        }));
    };

    let previewProperties;
    if (isPending || isInvitation) {
        if (isAdmin || allProperties) {
            previewProperties = currentUser.availableProperties;
        } else {
            previewProperties = currentUser.properties;
        }
    } else {
        previewProperties = currentUser.properties;
    }

    return (
        <div>
            {isInvitation && (
                <Spacer marginY="sm">
                    <Text as="div">
                        {`Please review ${currentUser.userName}'s access to the
                            properties and the features of this company account.`}
                    </Text>
                </Spacer>
            )}
            <Row>
                <Col md={12} lg={6}>
                    <div className="AddUserPage-preview-values">
                        <Text as="div">
                            <span className="AddUserPage-bold">Email: </span>
                            {currentUser.email}
                            {currentUser.isPending && (
                                <React.Fragment>
                                    <Spacer marginX="xs" as="span">
                                        <Tag appearance="gray" size="sm">Pending</Tag>
                                    </Spacer>
                                    <TextButton onClick={handleCancelInvitation}>Cancel Invite</TextButton>
                                </React.Fragment>
                            )}
                            {isYou && (
                                <Spacer marginX="xs" as="span">
                                    <Tag appearance="gray" size="sm">You</Tag>
                                </Spacer>
                            )}
                        </Text>
                        <Text as="div">
                            <span className="AddUserPage-bold">Name: </span>
                            {currentUser.userName}
                        </Text>
                        <Text as="div">
                            <span className="AddUserPage-bold">Role: </span>
                            {isAdmin ? 'Company admin' : 'User'}
                            {!isYou && (
                                <Spacer marginX="xs" as="span">
                                    <TextButton onClick={onEditRole}>Edit</TextButton>
                                </Spacer>
                            )}
                        </Text>
                        <Text as="div">
                            <span className="AddUserPage-bold">Access to: </span>
                            {allProperties ? 'All properties' : 'Limited Properties'}
                            {!isYou && !isAdmin && (
                                <Spacer marginX="xs" as="span">
                                    <TextButton onClick={onEditAllProperties}>Edit</TextButton>
                                </Spacer>
                            )}
                        </Text>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={6}>
                    <div className="AddUserPage-list-box">
                        <div className="AddUserPage-list-box-header">
                            <Text as="div">
                                {`${previewProperties.length} Properties`}
                            </Text>
                            {!isYou && !allProperties && (
                                <TextButton onClick={onEditProperties}>
                                    Edit
                                </TextButton>
                            )}
                            {allProperties && (
                                <Text>
                                    {`${currentUser.userName} will have access to any properties added in the future.`}
                                </Text>
                            )}
                        </div>
                        <div className="AddUserPage-list-box-body">
                            {previewProperties.map((property, i) => (
                                <div key={i} className="AddUserPage-list-box-row">
                                    <div className="AddUserPage-list-box-col">
                                        {property.name || property.street}
                                    </div>
                                    <div className="AddUserPage-list-box-col">
                                        {property.city}
                                    </div>
                                    <div className="AddUserPage-list-box-col">
                                        {property.state}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col md={12} lg={6}>
                    <div className="AddUserPage-list-box">
                        <div className="AddUserPage-list-box-header">
                            <Text as="div">
                                {`${currentUser.userName}'s Permissions`}
                            </Text>
                            {!isYou && !isAdmin && (
                                <TextButton onClick={onEditPermissions}>
                                    Edit
                                </TextButton>
                            )}
                        </div>
                        <div className="AddUserPage-list-box-body">
                            {Object.keys(otherPermissions).map(permission => (
                                <div key={permission} className="AddUserPage-list-box-item">
                                    {currentUser.permissions[permission] || isAdmin ?
                                        <IconCheckmark size={16} className="permission-icon" />
                                        : <IconClose size={16} className="permission-icon" />}
                                    {UserPermissionsConstants.DESCRIPTION[permission].DESCRIPTION}
                                </div>
                            ))}
                        </div>
                    </div>
                </Col>
            </Row>
            {isInvitation && (
                <React.Fragment>
                    <HR />
                    <div className="Questions-footer">
                        <TextButton className="Questions-button" onClick={onCancel}>
                            Cancel
                        </TextButton>
                        {isInvitation && (
                            <div>
                                <TextButton className="Questions-button" onClick={onBack}>
                                    Back
                                </TextButton>
                                <Button buttonType="primary" onClick={onSendInvite}>
                                    Send Invitation
                                </Button>
                            </div>
                        )}
                    </div>
                </React.Fragment>
            )}
            {error && (
                <Row>
                    <Col md={12} lg={6}>
                        <Alert appearance="error" body={error} />
                    </Col>
                </Row>
            )}
            {!isYou && !isInvitation && !isPending && (
                <div className="AddUserPage-deactivate">
                    <Text as="div">
                        {`Do you want to remove ${currentUser.userName} from the company account
                        and revoke their access to properties and features?`}
                    </Text>
                    <Button onClick={handleDeactivateAccount}>
                        Deactivate Account
                    </Button>
                </div>
            )}
        </div>
    );
};

Preview.propTypes = propTypes;

const mapStateToProps = state => ({
    currentUser: UserManagementSelectors.makeCurrentUser(state),
    user: UserSelectors.makeUser(state),
});

export default connect(mapStateToProps)(Preview);
